import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import AddAlarmGroupModal from '@zeus/core/modals/AddAlarmGroupModal';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import useFlowUrl from '_/features/flows/common/hooks/useFlowUrl';
import { useAlarmGroups, useCreateAlarmGroup } from '_/hooks/useAlarmGroups';
import useOrgAccess from '_/hooks/useOrgAccess';
import { filterErrorsForField } from '_/utils/formValidations';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState, useMemo, useEffect, useCallback, ReactElement } from 'react';

interface AlarmGroup {
  id: string;
  name: string;
  createdOn: string;
}

function FlowAlarmGroups(): ReactElement {
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string; projectSlug: string }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { flowId, flowSlug, selectedFlow } = useFlowContext();
  const { data: alarmGroups = [], refetch, isRefetching, isLoading } = useAlarmGroups(flowId);
  const { mutate: createAlarmGroup, error: createError, isError } = useCreateAlarmGroup(flowId);
  const alarmGroupUrl = useFlowUrl('/alarmgroups');
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [alarmGroupName, setAlarmGroupName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  useEffect(() => {
    return () => {
      setAlarmGroupName('');
      setAddModalOpen(false);
      setValidationErrors([]);
    };
  }, []);

  // Convert API error to validation error format
  useEffect(() => {
    if (isError && createError) {
      const errors = (createError as any)?.messages || [];
      setValidationErrors(errors);
    }
  }, [isError, createError]);

  const handleNameChange = (name: string) => {
    setAlarmGroupName(name);
    // Clear errors when user types
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleNewAlarmGroup = useCallback(() => {
    setAddModalOpen(true);
  }, []);

  const resetAlarmGroup = () => {
    setAlarmGroupName('');
    setValidationErrors([]);
  };

  async function handleSaveAlarmGroup() {
    if (flowId && alarmGroupName) {
      const newAlarmGroup = {
        flowId: flowId,
        name: alarmGroupName,
      };

      createAlarmGroup(newAlarmGroup, {
        onSuccess: () => {
          setAddModalOpen(false);
          resetAlarmGroup();
        },
      });
    }
  }

  async function handleCancelAlarmGroup() {
    setAddModalOpen(false);
    resetAlarmGroup();
  }

  const alarmGroupErrors = useMemo(() => filterErrorsForField(validationErrors, 'name'), [validationErrors]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRowClick = (record: AlarmGroup) => {
    // Navigate to the selected alarm group
    void history({ to: `${alarmGroupUrl}/${record.id}` });
  };

  // Filter alarm groups based on search text
  const filteredAlarmGroups = alarmGroups.filter((alarmGroup) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return alarmGroup.name?.toLowerCase().includes(lowerSearch) || alarmGroup.id?.toLowerCase().includes(lowerSearch);
  });

  const columns: ColumnsType<AlarmGroup> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: AlarmGroup) => (
        <a onClick={() => handleRowClick(record)} style={{ cursor: 'pointer' }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sorter: (a, b) => dayjs(a.createdOn).unix() - dayjs(b.createdOn).unix(),
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
  ];

  // Set up breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}`,
      },
      { title: 'Alarm Groups' },
    ]);

    setActionButtons([
      {
        onClick: handleNewAlarmGroup,
        icon: <PlusCircleOutlined />,
        label: 'Add',
        tooltip: access ? 'Add an Alarm Group' : 'You do not have the right permissions to add an alarm group',
        disabled: !access,
      },
    ]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    selectedOrganization?.name,
    orgSlug,
    selectedProject?.name,
    projectSlug,
    selectedFlow?.name,
    flowSlug,
    access,
    handleNewAlarmGroup,
  ]);

  // Set up refresh functionality
  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, handleRefresh, isRefetching]);

  return (
    <PageContent>
      <Input
        placeholder="Search alarm groups..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 400 }}
        allowClear
      />
      <Table
        columns={columns}
        dataSource={filteredAlarmGroups}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <AddAlarmGroupModal
        alarmGroups={alarmGroups}
        alarmGroupErrors={alarmGroupErrors}
        open={addModalOpen}
        handleSave={handleSaveAlarmGroup}
        handleCancel={handleCancelAlarmGroup}
        handleChange={handleNameChange}
        name={alarmGroupName}
        title="Create Alarm Group"
      />
    </PageContent>
  );
}

export default FlowAlarmGroups;
